import React from 'react';
import {
    Button,
    Text,
    makeStyles,
    shorthands,
    Spinner,
} from '@fluentui/react-components';
import { EditRegular, ArrowSyncRegular } from '@fluentui/react-icons';
import Editor from 'react-simple-code-editor';
import { Highlight, Prism, themes } from 'prism-react-renderer';
import { useTokenService } from '../../tokenService';
import { ChatScriptResponse, CopilotService, AnalyticsRawResult } from '../../Service/ServerAPI';
import { RootState } from '../../utils/store';
import { useSelector } from 'react-redux';
import ResultsDialogWrapper from '../CampaignGPT/ResultsModalWrapper';
import AnalyticsResultsDialog from '../CampaignGPT/AnalyticsResultsDialog';

interface ScriptProps {
    script: string;
    isAnalytics?: boolean;
}

const useStyles = makeStyles({
    paper: {
        boxShadow: 'none',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#3A3E45',
        padding: '16px',
        border: 'none',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#3A3E45',
        borderBottom: '1px solid #6B7584',
        padding: '8px 0',
    },
    title: {
        fontFamily: 'Segoe UI',
        fontWeight: 'bold',
        fontSize: '1rem',
        color: '#fff',
    },
    buttonContainer: {
        display: 'flex',
        gap: '8px',
    },
    editButton: {
        backgroundColor: '#007738',
        border: '1px solid #008746',
        borderRadius: '4px',
        padding: '1px 8px',
        fontSize: '.7rem',
        color: '#fff',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px',
        '&:hover': {
            backgroundColor: '#008746',
            border: '1px solid #008746',
        },
    },
    rerunButton: {
        backgroundColor: '#0078D4',
        border: '1px solid #0078D4',
        borderRadius: '4px',
        padding: '1px 8px',
        fontSize: '.7rem',
        color: '#fff',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px',
        '&:hover': {
            backgroundColor: '#005A9E',
            border: '1px solid #005A9E',
        },
    },
    rerunButtonDisabled: {
        backgroundColor: '#c7c7c7',
        border: '1px solid #c7c7c7',
        borderRadius: '4px',
        padding: '1px 8px',
        fontSize: '.7rem',
        color: '#fff',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px',
        position: 'relative', // Needed for the spinner positioning
    },
    spinnerContainer: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    },
    editorContainer: {
        padding: '8px 0',
        borderTop: '1px solid #6B7584',
        maxHeight: '800px',
        overflow: 'auto',
    },
    editor: {
        fontFamily: "'Fira Mono', Consolas, 'Courier New', monospace",
        backgroundColor: '#3A3E45',
        overflow: 'auto',
        padding: '10px',
    },
});

const Script: React.FC<ScriptProps> = ({ script, isAnalytics = false }) => {
    const classes = useStyles();
    const [localScript, setScript] = React.useState(script);
    const [isEditing, setIsEditing] = React.useState(false);
    const [showDialog, setShowDialog] = React.useState(false);
    const [scriptResult, setScriptResult] =
        React.useState<ChatScriptResponse | null>(null);
    const [analyticsResult, setAnalyticsResult] =
        React.useState<AnalyticsRawResult | null>(null);
    const [isScriptRunning, setIsScriptRunning] = React.useState(false);

    const getAccessToken = useTokenService();
    const copilotService = CopilotService.getInstance(getAccessToken);
    const settings = useSelector((state: RootState) => state.settings);

    const runScript = (force: boolean = true) => {
        setIsScriptRunning(true);
        const dataset = settings.sessionDataset || settings.dataset;

        if (isAnalytics) {
            // Analytics scripts (analyzeUser/emit) must run via the analytics
            // endpoint, which returns raw emitted counts (no GPT interpretation).
            copilotService
                .executeAnalyticsScriptAPI(localScript, settings.apiEndpoint, dataset)
                .then((response) => {
                    setAnalyticsResult(response);
                    setShowDialog(true);
                    setIsScriptRunning(false);
                })
                .catch(() => {
                    setIsScriptRunning(false);
                });
            return;
        }

        copilotService
            .executeAdhocScriptAPI(localScript, [], settings.apiEndpoint, dataset)
            .then((response) => {
                // Create a new object from the response, excluding the properties you don't want
                //const newResponse = { ...response };
                setScriptResult(response);
                setShowDialog(true);
                setIsScriptRunning(false);
                //delete newResponse.script;
                //delete newResponse.sampleUsers;
                //setScriptResult(JSON.stringify(newResponse, null, 2)); // assuming response is a JSON object
                //setScriptResult(response); // assuming response is a JSON object

                /*if (response) {
                    dispatch(
                        setScriptMessage([
                            ...settings.chat,
                            {
                                script: script,
                                role: 'User',
                                endpoint: 'executeScript',
                            },
                            response,
                        ])
                    );
                }*/
            })
            .catch(() => {
                setIsScriptRunning(false); // Ensure the script running state is set to false if there's an error
            });
    };

    const handleScriptChange = (newScript: string) => {
        setScript(newScript);
    };

    const handleEditClick = () => {
        setIsEditing(!isEditing);
    };

    const handleOpenDialog = () => {
        runScript();
        //setShowDialog(true);
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    const LuaEditor: React.FC = () => (
        <Highlight
            theme={themes.vsDark}
            code={localScript ?? '--None'}
            language="lua"
        >
            {({ tokens, getLineProps, getTokenProps }) => (
                <>
                    {tokens.map((line, i) => (
                        <div {...getLineProps({ line, key: i })}>
                            {line.map((token, key) => (
                                <span {...getTokenProps({ token, key })} />
                            ))}
                        </div>
                    ))}
                </>
            )}
        </Highlight>
    );

    return (
        <div>
            <div>
                {isAnalytics ? (
                    <AnalyticsResultsDialog
                        result={analyticsResult}
                        isScriptRunning={false}
                        showDialog={showDialog}
                        onClose={handleCloseDialog}
                    />
                ) : (
                    <ResultsDialogWrapper
                        result={scriptResult}
                        isScriptRunning={false}
                        showDialog={showDialog}
                        onClose={handleCloseDialog}
                    />
                )}
            </div>
            <div className={classes.paper}>
                <div className={classes.header}>
                    <Text className={classes.title}>Script</Text>
                    <div className={classes.buttonContainer}>
                        <Button
                            className={
                                isScriptRunning
                                    ? classes.rerunButtonDisabled
                                    : classes.rerunButton
                            }
                            onClick={handleOpenDialog}
                            disabled={isScriptRunning}
                            icon={
                                isScriptRunning ? undefined : (
                                    <ArrowSyncRegular />
                                )
                            } // Conditionally render the icon
                        >
                            {isScriptRunning ? (
                                <>
                                    <div className={classes.spinnerContainer}>
                                        <Spinner
                                            label="Running...."
                                            size="small"
                                        />
                                    </div>
                                    <span style={{ visibility: 'hidden' }}>
                                        Rerun Script
                                    </span>
                                </>
                            ) : (
                                'Rerun Script'
                            )}
                        </Button>
                        <Button
                            appearance="primary"
                            icon={<EditRegular />}
                            onClick={handleEditClick}
                            className={classes.editButton}
                            aria-label="edit script"
                        >
                            {isEditing ? 'EDITING...' : 'EDIT SCRIPT'}
                        </Button>
                    </div>
                </div>
                <div className={classes.editorContainer}>
                    <Editor
                        value={localScript ?? ''}
                        onValueChange={handleScriptChange}
                        highlight={LuaEditor}
                        padding={10}
                        disabled={!isEditing}
                        className={classes.editor}
                    />
                </div>
            </div>
        </div>
    );
};

export default Script;
